import { formatNumber } from '@angular/common';
import {
  AfterViewInit,
  Component,
  effect,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  output,
  signal
} from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { VANILLA_STRIP } from '@approot/constants';
import {
  AdjustQuoteForMargin,
  BidAskByDateType,
  GenericLeg,
  PremiumStyle,
  QuotePair,
  RFQ_QUOTE_CONFIRM_RFE_ACCEPT,
  RfqBidOfferQuotes,
  RfqQuote,
  RfqSetup,
  SubAccount,
  TradeBookingParametersHedge,
  TradeBookingParametersLeg,
  Vol
} from '@approot/models';
import {
  FranchiseCredit,
  VolBasedRfqOptionPriceV2Response
} from '@approot/provider-quote/';
import { RfqMessagesService } from '@approot/rfq-messages/rfq-messages.service';
import {
  AlertsService,
  BookingTradeState,
  ClientQuoteEventExtended,
  ConfigService,
  EntityMapperService,
  HedgeQuote,
  HedgeQuoteMessage,
  PhoenixService,
  ProductsConfigService,
  RfqService,
  RfqTabService,
  RfqTradeCaptureParams,
  TradesCaptureService
} from '@approot/services';
import {
  getReferenceCounterNotional,
  getReferenceNotional
} from '@approot/services/rfq/rfq-tab/rfq-tab';
import { sleep, SubscriptionSink, Utilities } from '@approot/utilities';
import { clone, cloneDeep, isEqual } from 'lodash';
import { forkJoin, Observable, of } from 'rxjs';
import { RfqPricesComponent } from '../rfq-prices/rfq-prices.component';
import {
  cleanBookedHedges,
  getClientPremium,
  nonVolBasedCashPremium,
  nonVolBasedPremiumStylesPremiums
} from './rfq-book-trades.funcs';

const { cleanFormattedNumeric } = Utilities;

interface LegPremium {
  clientAction: 'pays' | 'rcvs';
  premiumValue: number;
  margin: number;
  premiumCurrency: string;
  clientPremium: number;
  cashPremium: number;
  cashToPremiumStyleDividingAmount: number;
}

interface SaveDealPayload {
  task: 'saveDeal';
  trades: (TradeBookingParametersLeg | TradeBookingParametersHedge)[];
  premiumCurrency: string;
  premiumStyle: string;
  subAccount: SubAccount;
  franchiseCredit: {
    franchiseCreditInFranchiseCreditCurrency: number;
    franchiseCreditCurrency: string;
  };
  bookedHedgeRate?: number;
  bookedHedgeAmount?: number;
  hedgeBooking?: HedgeQuoteMessage;
}

interface SaveNonVolBasedDealPayload {
  task: 'saveNonVolBasedDeal';
  legsPremiums: LegPremium[];
  legs: GenericLeg[];
  setup: RfqSetup;
  confirmRFEAccept: ClientQuoteEventExtended;
  dealDirectionIsBuy: boolean;
  clientTradeCaptureParams?: Record<string, boolean | string>;
  bookedHedgeRate?: number;
  bookedHedgeAmount?: number;
  hedgeBooking?: HedgeQuoteMessage;
}

@Component({
  selector: 'app-rfq-book-trades',
  templateUrl: './rfq-book-trades.component.html',
  styleUrls: ['./rfq-book-trades.component.scss']
})
export class RfqBookTradesComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @Input() parentPricesComponent: RfqPricesComponent;
  @Input() volBased = true;
  @Input() public nonDxExists: boolean;
  public resetRfqContainerHeight = output();
  public stateChange = output<BookingTradeState>();
  public lastNewVolBasedQuoteResponse: VolBasedRfqOptionPriceV2Response;
  public acceptedQuoteForm: FormGroup = new FormGroup({
    legs: new FormArray([]),
    franchiseCreditCurrency: new FormControl(''),
    franchiseCreditInFranchiseCreditCurrency: new FormControl(''),
    franchiseCreditInPremiumCurrency: new FormControl('')
  });
  public state = signal<BookingTradeState>('INITIAL');
  public premiumStyle: PremiumStyle;
  public marginClass: string[] = [];
  public clientPremiumClass: string[] = [];
  public franchiseCredit: FranchiseCredit;
  public premiumCurrency: string;
  public totalCashPremium: string = '';
  public totalMargin: string = '';
  public totalClientPremium: string = '';
  public totalClientPremiumDir: 'pays' | 'rcvs' = null;
  public totalCashPremiumDir: 'pays' | 'rcvs' = null;
  public totalMarginDir: 'pays' | 'rcvs' = null;
  public allPremiumDatesSame: boolean;
  public useRetrocessionFee = false;
  public rfqTradeCaptureParams: RfqTradeCaptureParams[] = null;
  public rfqTradeCaptureParamsForm: FormGroup<any> = new FormGroup({});
  public premiums: string[] = [];
  public currencyPair: QuotePair;
  public hedgeEditingEnable = false;
  public fixingSourceCodes: string[] = [];
  public fixingSourceCodeToLongNameMap: Record<string, string> = {};
  public tradeBookingPremiumStyles: ('Amount' | 'pips' | 'percent')[] = [];
  public isGenericMultiHedge = false;
  public productType: string;
  public showConfirmationType = false;
  private rfqMessageService = inject(RfqMessagesService);
  private volumeCreditMessage: { volumeCredit: number; currency: string };
  private AdjustedQuoteForMargin: AdjustQuoteForMargin;
  private hedgeQuotes: HedgeQuote[];
  private subsink = new SubscriptionSink();

  constructor(
    private rfqService: RfqService,
    private rfqTabService: RfqTabService,
    private alertsService: AlertsService,
    private tradesCaptureService: TradesCaptureService,
    private elementRef: ElementRef,
    private phxService: PhoenixService,
    private entityMapperService: EntityMapperService,
    private configService: ConfigService,
    private productsConfigService: ProductsConfigService
  ) {
    effect(() => {
      const s = this.state();
      this.stateChange.emit(s);
    });

    effect(() => {
      const msg: any = this.rfqMessageService.message().volumeCredit?.content;
      if (msg && !isEqual(msg, this.volumeCreditMessage)) {
        this.volumeCreditMessage = msg;
        this.setVolumeCredit();
      }
    });

    const leg = this.rfqTabService.executedTradeInfo.legs[0];
    this.productType = leg.productType;
  }

  public ngOnInit() {
    this.useRetrocessionFee =
      this.configService.userSettings()?.useRetrocessionFee;
    this.currencyPair = this.rfqTabService.tabData.title.quotePair;
    const cp = this.currencyPair.base + this.currencyPair.term;
    const cpi =
      this.productsConfigService.cutOffTimesAndFixingSources.currencyPairInfo.find(
        c => c.currencyPair === cp
      );
    this.fixingSourceCodes = cpi?.fixingSources || [];
    this.isGenericMultiHedge =
      this.rfqTabService.executionContextType() === 'genericMultiHedge';
    const fs = this.configService.userSettings().fixingsource;
    this.fixingSourceCodeToLongNameMap = fs.reduce((acc, f) => {
      acc[f.code] = f.longName;
      return acc;
    }, {});
    this.rfqTabService.canCloseWindow = false;
    this.enableHedgeEditing();
    if (this.volBased) {
      this.lastNewVolBasedQuoteResponse =
        this.rfqTabService.lastNewVolBasedQuoteResponse;
      this.franchiseCredit = this.lastNewVolBasedQuoteResponse.franchiseCredit;
      const fmt = (n: number) => formatNumber(n, 'en-GB', '1.2-2');

      const formValue = this.acceptedQuoteForm.getRawValue();

      this.acceptedQuoteForm.setValue({
        ...formValue,
        franchiseCreditInFranchiseCreditCurrency: fmt(
          this.franchiseCredit.franchiseCreditInFranchiseCreditCurrency
        ),
        franchiseCreditCurrency: this.franchiseCredit.franchiseCreditCurrency,
        franchiseCreditInPremiumCurrency: fmt(
          this.franchiseCredit.franchiseCreditInPremiumCurrency
        )
      });
    }
    this.setUpNonVolBasedMode();
  }

  public ngAfterViewInit(): void {
    const el = this.elementRef.nativeElement as HTMLElement;
    const input = el.querySelector('.margin input') as HTMLInputElement;
    input?.focus();
    input?.select();
    if (this.volBased) {
      this.buildVolBasedBookingPremiums();
    }
    this.resetRfqContainerHeight.emit();
  }
  public ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  public invertDirection(dir: 'Buy' | 'Sell'): 'Buy' | 'Sell' {
    return dir === 'Buy' ? 'Sell' : 'Buy';
  }

  private async setVolumeCredit() {
    // Occasionally race condition where server sends the message before the form is ready
    for (let i = 0; i < 10; i++) {
      const volumeCreditCtrl =
        this.rfqTradeCaptureParamsForm.get('volumeCredit');

      const volumeCreditCurrencyCtrl = this.rfqTradeCaptureParamsForm.get(
        'volumeCreditCurrency'
      );
      if (volumeCreditCurrencyCtrl) {
        volumeCreditCurrencyCtrl.setValue(this.volumeCreditMessage.currency);
      }
      if (volumeCreditCtrl) {
        volumeCreditCtrl.setValue(this.volumeCreditMessage.volumeCredit);
        break;
      }

      await sleep(1000);
    }
  }

  private setUpNonVolBasedMode(): void {
    if (this.volBased) {
      return;
    }

    forkJoin({
      adjustQuoteForMargin: this.adjustQuoteForMargin(),
      rfqTradeCaptureParams: this.rfqService.getRfqTradeCaptureParams()
    }).subscribe(response => {
      if (response.rfqTradeCaptureParams) {
        this.handleRfqTradeCaptureParams(response.rfqTradeCaptureParams);
      }

      if (response.adjustQuoteForMargin) {
        this.AdjustedQuoteForMargin = response.adjustQuoteForMargin;
        this.buildNonVolBasedBookingPremiums();
      }
    });
  }

  private handleRfqTradeCaptureParams(params: RfqTradeCaptureParams[]): void {
    this.rfqTradeCaptureParams = clone(params).filter(
      p => p.shortName !== 'volumeCreditCurrency'
    );

    const confirmation = this.rfqTradeCaptureParams.find(
      p => p.shortName === 'confirmationType'
    );
    if (confirmation?.['productTypes'].includes(this.productType)) {
      this.showConfirmationType = true;
    }
    if (!this.showConfirmationType) {
      this.rfqTradeCaptureParams = this.rfqTradeCaptureParams.filter(
        p => p.shortName !== 'confirmationType'
      );
    }

    const forceCashSettlement = params['forceCashSettlement'];
    const tabTitle = this.rfqTabService.tabData.title;
    if (tabTitle.quotePair) {
      this.premiums = [tabTitle.quotePair.base, tabTitle.quotePair.term];
    }

    params.forEach(param => {
      let required = false;
      let disabled = false;
      let value: string | boolean | number = '';
      switch (param.type) {
        case 'enum':
          value = param.values[0] || '';
          break;
        case 'boolean':
          value = param.default || false;
          break;
        default:
          if (param.shortName === 'forcedCashSettlementFixingSource') {
            value = null;
          } else if (param.shortName === 'volumeCredit') {
            value = this.volumeCreditMessage?.volumeCredit;
            if (value != null && typeof value !== 'boolean') {
              value = formatNumber(+value, 'en-GB', '1.2-2');
            }
          } else {
            value = this.premiums?.[0] || '';
          }
          if (
            !forceCashSettlement &&
            param.shortName !== 'volumeCredit' &&
            param.shortName !== 'tradeBookingPremiumStyle' &&
            param.shortName !== 'tradeBookingPremiumCurrency' &&
            param.shortName !== 'confirmationType'
          ) {
            disabled = true;
          }
      }

      if (param.readOnly) {
        disabled = true;
      }

      if (param.shortName === 'tradeBookingPremiumCurrency') {
        value = this.rfqTabService.setupData.premium;
      }
      const validators = required ? [Validators.required] : undefined;
      const ctrl = new FormControl(value, validators);
      if (disabled) {
        ctrl.disable();
      }
      this.rfqTradeCaptureParamsForm.addControl(param.shortName, ctrl);
    });
    this.handleTradingBookPremiumStyles();
    this.rfqTradeCaptureParamsForm.valueChanges.subscribe(() => {
      const forceCashSettlement =
        this.rfqTradeCaptureParamsForm.getRawValue()['forceCashSettlement'];

      [
        'forcedCashSettlementFixingSource',
        'forcedCashSettlementCurrency'
      ].forEach(field => {
        if (forceCashSettlement) {
          this.rfqTradeCaptureParamsForm
            .get(field)
            ?.enable({ emitEvent: false });
        } else {
          this.rfqTradeCaptureParamsForm
            .get(field)
            ?.disable({ emitEvent: false });
        }
      });

      const ctrl = this.rfqTradeCaptureParamsForm.get(
        'forcedCashSettlementFixingSource'
      );

      if (forceCashSettlement) {
        if (!ctrl.value) {
          ctrl.setValue(this.fixingSourceCodes[0], { emitEvent: false });
        }
      } else {
        ctrl.setValue(null, { emitEvent: false });
      }
    });
  }

  public premiumStyleChange(value: 'Amount' | 'pips' | 'percent'): void {
    this.premiumStyle = value;
    this.buildNonVolBasedBookingPremiums(true);
  }

  public tradeBookingPremiumCurrencyChange(currency: string): void {
    this.setTradeBookingPremiumStyles(currency);
    const ctrl = this.rfqTradeCaptureParamsForm.get('tradeBookingPremiumStyle');
    this.premiumStyle = 'Amount';
    ctrl.setValue('Amount');
    this.buildNonVolBasedBookingPremiums(true);
  }

  private handleTradingBookPremiumStyles(): void {
    const ctrl = this.rfqTradeCaptureParamsForm.get('tradeBookingPremiumStyle');
    if (!ctrl) {
      return;
    }

    const currency =
      this.rfqTradeCaptureParamsForm.get('tradeBookingPremiumCurrency')
        ?.value || this.rfqTabService.setupData.premium;
    this.setTradeBookingPremiumStyles(currency);
    ctrl.setValue('Amount');
    this.premiumStyle = 'Amount';
  }

  private setTradeBookingPremiumStyles(currency: string): void {
    if (
      currency === this.rfqTabService.tabData.title.quotePair.base &&
      this.productType !== VANILLA_STRIP
    ) {
      this.tradeBookingPremiumStyles = ['Amount', 'percent'];
    } else {
      this.tradeBookingPremiumStyles = ['Amount', 'percent', 'pips'];
    }
  }

  private legConversionRate(legIndex: number): number {
    const latestQuoteEvent = this.latestQuoteEvent();
    const leg = latestQuoteEvent.quote.legs[legIndex];
    return (leg.spotBid + leg.spotOffer) / 2;
  }

  private latestQuoteEvent(): ClientQuoteEventExtended {
    return Array.from(this.rfqTabService.lastQuoteEvents())[
      this.rfqTabService.lastQuoteEvents().size - 1
    ]?.[1];
  }

  /*
      If the premium direction is “Client Pays” then Client Premium is
      calculated as Trading Premium + Margin

      If the premium direction is “Client Receives” then Client Premium
      is calculated as Trading Premium - Margin.
      Raise exception if this is < 0
   */
  public marginChange(i: number) {
    const { premium, margin, dir, ctrls, retrocessionFee } = this.getValues(i);
    if (isNaN(margin)) {
      // minor in case users enter invalid values e.g '4z'
      ctrls.get('margin').setValue('');
      return;
    }
    const clientPremium = getClientPremium(
      premium,
      margin,
      dir,
      retrocessionFee
    );
    const marginStr = this.roundNumberToDp(
      margin,
      this.premiumStyle === 'Amount' ? 2 : null
    );

    ctrls
      .get('clientPremium')
      .setValue(
        this.roundNumberToDp(
          clientPremium,
          Math.max(marginStr.split('.')[1]?.length || 0)
        ),
        {
          emitEvent: false
        }
      );

    ctrls.get('margin').setValue(marginStr, { emitEvent: false });

    this.updateClasses(clientPremium, margin, i);
    this.updateCashPremium(i);
    this.updateTotals();

    this.acceptedQuoteForm.markAsPristine();
  }

  /*
      Value < 0 is not accepted
      If the premium direction is “Client Pays” then the Margin is
      calculated as Client Premium - Trading Premium. Display in orange if -ve

      If the premium direction is “Client Receives” then the Margin is calculated
      as Trading Premium - Client Premium. Display in orange if -ve
  */
  public clientPremiumChange(i: number) {
    const { premium, clientPremium, dir, ctrls, retrocessionFee } =
      this.getValues(i);
    if (isNaN(clientPremium)) {
      // minor in case users enter invalid values e.g '4z'
      ctrls.get('clientPremium').setValue('');
      return;
    }
    const clientPremiumStr = this.roundNumberToDp(
      clientPremium,
      this.premiumStyle === 'Amount' ? 2 : null
    );

    const margin = (+clientPremium - +premium) * dir - retrocessionFee;
    const marginStr = this.roundNumberToDp(
      margin,
      Math.max(clientPremiumStr.split('.')[1]?.length || 0)
    );

    ctrls.get('margin').setValue(marginStr, { emitEvent: false });

    ctrls.get('clientPremium').setValue(clientPremiumStr, {
      emitEvent: false
    });

    this.marginClass[i] = +margin < 0 ? 'negative' : 'positive';
    this.clientPremiumClass[i] = +clientPremium < 0 ? 'invalid' : 'valid';
    this.updateCashPremium(i);
    this.updateTotals();

    this.acceptedQuoteForm.markAsPristine();
  }

  public retrocessionFeeChange(i: number) {
    const { premium, clientPremium, dir, ctrls, retrocessionFee } =
      this.getValues(i);
    if (isNaN(retrocessionFee)) {
      ctrls.get('retrocessionFee').setValue('');
      return;
    }
    const clientPremiumStr = this.roundNumberToDp(
      clientPremium,
      this.premiumStyle === 'Amount' ? 2 : null
    );

    const margin = (+clientPremium - +premium) * dir - retrocessionFee;
    const marginStr = this.roundNumberToDp(
      margin,
      Math.max(clientPremiumStr.split('.')[1]?.length || 0)
    );

    ctrls.get('margin').setValue(marginStr, { emitEvent: false });

    ctrls.get('clientPremium').setValue(clientPremiumStr, {
      emitEvent: false
    });

    this.marginClass[i] = +margin < 0 ? 'negative' : 'positive';
    this.clientPremiumClass[i] = +clientPremium < 0 ? 'invalid' : 'valid';
    this.updateCashPremium(i);
    this.updateTotals();

    this.acceptedQuoteForm.markAsPristine();
  }

  /**
   * (change) is not called if user edits a field but ultimately does not change it
   * so the form is still marked as dirty.   Here's a partial workaround so either
   * <Enter> or (blur) will trigger the change event.
   */
  public onMarginKeyPress(event: KeyboardEvent, i: number) {
    if (event.key === 'Enter') {
      this.marginChange(i);
    }
  }

  public onPremiumKeyPress(event: KeyboardEvent, i: number) {
    if (event.key === 'Enter') {
      this.clientPremiumChange(i);
    }
  }

  public onRetrocessionFeeKeyPress(event: KeyboardEvent, i: number) {
    if (event.key === 'Enter') {
      this.retrocessionFeeChange(i);
    }
  }

  public onMarginBlur(i: number) {
    this.marginChange(i);
  }

  public onRetrocessionFeeBlur(i: number) {
    this.retrocessionFeeChange(i);
  }

  public onPremiumBlur(i: number) {
    this.clientPremiumChange(i);
  }

  public onHedgeBlur(): void {
    this.acceptedQuoteForm.markAsPristine();
  }

  /*
    If user over-writes the franchiseCreditInPremiumCurrency then recalculate the
    franchiseCreditInFranchiseCreditCurrency as
      franchiseCreditInPremiumCurrency x conversionRateFromPremiumCurrencyIntoFranchiseCreditCurrency
  */
  public onFranchiseCreditInPremiumCurrencyChange() {
    const creditCurrencyCtrl = this.acceptedQuoteForm.get(
      'franchiseCreditInFranchiseCreditCurrency'
    );

    if (!creditCurrencyCtrl) {
      return;
    }
    const franchiseCreditInPremiumCurrency = cleanFormattedNumeric(
      this.acceptedQuoteForm.get('franchiseCreditInPremiumCurrency').value
    );
    const franchiseCreditInFranchiseCreditCurrency =
      franchiseCreditInPremiumCurrency *
      this.franchiseCredit
        .conversionRateFromPremiumCurrencyIntoFranchiseCreditCurrency;

    creditCurrencyCtrl.setValue(
      formatNumber(franchiseCreditInFranchiseCreditCurrency, 'en-GB', '1.2-2')
    );
    this.acceptedQuoteForm.markAsPristine();
  }

  /*
    If user over-writes the franchiseCreditInFranchiseCreditCurrency then recalculate the
    franchiseCreditInPremiumCurrency as
      franchiseCreditInFranchiseCreditCurrency / conversionRateFromPremiumCurrencyIntoFranchiseCreditCurrency
  */
  public onFranchiseCreditInFranchiseCreditCurrencyChange() {
    const premiumCurrencyCtrl = this.acceptedQuoteForm.get(
      'franchiseCreditInPremiumCurrency'
    );

    if (!premiumCurrencyCtrl) {
      return;
    }
    const franchiseCreditInFranchiseCreditCurrency = cleanFormattedNumeric(
      this.acceptedQuoteForm.get('franchiseCreditInFranchiseCreditCurrency')
        .value
    );
    const franchiseCreditInPremiumCurrency =
      franchiseCreditInFranchiseCreditCurrency /
      this.franchiseCredit
        .conversionRateFromPremiumCurrencyIntoFranchiseCreditCurrency;
    premiumCurrencyCtrl.setValue(
      formatNumber(franchiseCreditInPremiumCurrency, 'en-GB', '1.2-2')
    );
    this.acceptedQuoteForm.markAsPristine();
  }

  public get legForms() {
    return this.acceptedQuoteForm.controls.legs as FormArray<FormGroup>;
  }

  public get hedgeBooking(): FormArray<FormGroup> {
    return this.acceptedQuoteForm.controls.hedgeBooking as FormArray<FormGroup>;
  }

  public closeWindow() {
    this.rfqTabService.canCloseWindow = true;
    this.rfqTabService.closeWindow();
  }

  public get hasLastHandleVolBasedHedgeExecution() {
    return !!this.rfqTabService.lastHandleVolBasedHedgeExecution();
  }

  public setAllPremiumDatesSame(
    bidAsksByDate: BidAskByDateType[],
    vols: Vol[]
  ) {
    // Only show totals if all premium dates are the same and more than one row
    this.allPremiumDatesSame =
      vols.length > 1 && new Set(bidAsksByDate.map(v => v.date)).size === 1;
  }

  public onCreditBlur(
    creditAmountField:
      | 'franchiseCreditInFranchiseCreditCurrency'
      | 'franchiseCreditInPremiumCurrency'
  ): void {
    const ctrl = this.acceptedQuoteForm.get(creditAmountField);
    if (ctrl.value === '') {
      return;
    }

    ctrl.setValue(
      formatNumber(cleanFormattedNumeric(ctrl.value), 'en-GB', '1.2-2')
    );

    if (creditAmountField === 'franchiseCreditInFranchiseCreditCurrency') {
      this.onFranchiseCreditInFranchiseCreditCurrencyChange();
    } else {
      this.onFranchiseCreditInPremiumCurrencyChange();
    }
  }

  private get quotes(): RfqBidOfferQuotes {
    return this.rfqTabService.mungedDisplayedQuotes;
  }

  private buildVolBasedBookingPremiums(): void {
    const makeFormArray = (vols: Vol[]) => {
      const fArr = this.acceptedQuoteForm.get('legs') as FormArray;
      fArr.clear();
      vols.forEach(vol => {
        const val = formatNumber(
          Math.abs(vol.premium.value),
          'en-GB',
          this.numberFormat
        );
        const cashVal = formatNumber(
          Math.abs(vol.premium.cashValue),
          'en-GB',
          '1.2-2'
        );
        fArr.push(
          new FormGroup({
            clientAction: new FormControl(vol.premium.payOrReceive),
            premiumValue: new FormControl(val),
            margin: new FormControl(
              this.roundNumberToDp(0, this.premiumStyle === 'Amount' ? 2 : null)
            ),
            premiumCurrency: new FormControl(vol.premium.currency),
            clientPremium: new FormControl(val),
            cashPremium: new FormControl(cashVal),
            retrocessionFee: new FormControl(0),
            cashToPremiumStyleDividingAmount: new FormControl(
              vol.premium.cashToPremiumStyleDividingAmount
            )
          })
        );
      });
    };

    let bid = this.quotes.bids.find(row => {
      return row.status === RFQ_QUOTE_CONFIRM_RFE_ACCEPT;
    });

    let offer = this.quotes.offers.find(row => {
      return row.status === RFQ_QUOTE_CONFIRM_RFE_ACCEPT;
    });

    this.phxService.logEvent({
      type: 'rfe',
      category: 'acceptedQuote',
      quotes: this.quotes,
      bid,
      offer
    });

    if (bid) {
      this.premiumStyle = bid.premiumStyle;
      this.premiumCurrency = bid.bidAsk.currency;
      makeFormArray(bid.vol.left);
      this.setAllPremiumDatesSame(bid.bidAsksByDate, bid.vol.left);
      return;
    }

    if (offer) {
      this.premiumStyle = offer.premiumStyle;
      this.premiumCurrency = offer.bidAsk.currency;
      makeFormArray(offer.vol.right);
      this.setAllPremiumDatesSame(offer.bidAsksByDate, offer.vol.right);
      return;
    }
    console.error('Unable to initialize accepted quote');
    makeFormArray([]);
    this.updateTotals();
  }

  public bookTrade(): void {
    if (this.state() === 'BOOKING' || this.state() === 'BOOKED') {
      return;
    }

    this.state.set('BOOKING');

    if (this.volBased) {
      this.bookVolBasedTrade();
    } else {
      this.bookMarginTrade();
    }
    this.rfqTabService.canCloseWindow = false;
  }

  public getNonVolBasedPayload() {
    const legs = clone(this.rfqTabService.executedTradeInfo.legs);
    const legsPremiums: any[] = clone(
      this.acceptedQuoteForm.getRawValue().legs
    );
    legsPremiums.forEach((leg: any) => {
      const ctrl =
        this.rfqTradeCaptureParamsForm.controls.tradeBookingPremiumCurrency;
      if (ctrl) {
        leg.premiumCurrency = ctrl.value;
      } else if (!leg.premiumCurrency) {
        console.warn(
          "Unable to set premium currency. Missing form control for 'tradeBookingPremiumCurrency'",
          this.rfqTradeCaptureParamsForm.getRawValue()
        );
      }
    });

    const setup = clone(this.rfqTabService.executedTradeInfo.setup);
    setup.account = this.entityMapperService.getRealEntity(setup.account);
    delete setup.banks;
    legsPremiums.forEach((leg, index) => {
      leg.cashPremium = cleanFormattedNumeric(leg.cashPremium);
      leg.clientPremium = cleanFormattedNumeric(leg.clientPremium);
      leg.premiumValue = cleanFormattedNumeric(leg.premiumValue);
      leg.margin = cleanFormattedNumeric(leg.margin);
      leg.retrocessionFee = cleanFormattedNumeric(leg.retrocessionFee);
      leg.conversionRate = this.legConversionRate(index);
      if (
        this.rfqTradeCaptureParamsForm.get('tradeBookingPremiumStyle')
          ?.value === 'percent'
      ) {
        leg.clientPremium = leg.clientPremium / 100;
        leg.premiumValue = leg.premiumValue / 100;
        leg.margin = leg.margin / 100;
      }
    });
    const confirmRFEAccept =
      this.rfqTabService.executedTradeInfo.confirmRFEEvent;

    const clientTradeCaptureParams = this.rfqTradeCaptureParams
      ? this.rfqTradeCaptureParams.reduce((acc, param) => {
          let value = this.rfqTradeCaptureParamsForm.get(param.shortName)
            ?.value;
          if (
            typeof value === 'string' &&
            /^[\d,]+(\.\d*)?$/.test(value.trim())
          ) {
            value = cleanFormattedNumeric(value.trim());
          }
          acc[param.shortName] = value;
          return acc;
        }, {})
      : undefined;

    const bookedHedgeAmount = cleanFormattedNumeric(
      this.acceptedQuoteForm.get('bookedHedgeAmount')?.value
    );
    const bookedHedgeRate = this.acceptedQuoteForm.get('bookedHedgeRate');
    const isOffer = !!this.quotes.offers.find(
      quote => quote.status === RFQ_QUOTE_CONFIRM_RFE_ACCEPT
    );
    const payload: SaveNonVolBasedDealPayload = {
      task: 'saveNonVolBasedDeal',
      legsPremiums,
      legs,
      setup,
      confirmRFEAccept,
      clientTradeCaptureParams,
      bookedHedgeAmount,
      bookedHedgeRate: bookedHedgeRate ? +bookedHedgeRate.value : null,
      dealDirectionIsBuy: isOffer,
      hedgeBooking: this.getHedgeBooking()
    };

    return payload;
  }

  private bookMarginTrade(): void {
    const payload = this.getNonVolBasedPayload();
    this.saveDeal(payload);
  }

  private bookVolBasedTrade(): void {
    const { legs, hedges } = clone(
      this.rfqTabService.lastHandleVolBasedHedgeExecution()
    );

    const formValue = this.acceptedQuoteForm.getRawValue();
    const setupHedges = this.getHedges();

    hedges.forEach((hedge, i) => {
      const newNotional = +setupHedges.hedges[i].notional;
      const newStrike = +setupHedges.hedges[i].strike;

      if (!isNaN(newNotional)) {
        hedge.notional = newNotional;
      }
      if (!isNaN(newStrike)) {
        hedge.strike = newStrike;
      }
    });

    let trades = [...legs, ...hedges];

    trades.forEach(thing => (thing.isTradeConfirmed = true));

    legs.forEach((leg, i) => {
      leg.premium = cleanFormattedNumeric(formValue.legs[i].clientPremium);
      leg.tradingPremium = cleanFormattedNumeric(
        formValue.legs[i].premiumValue
      );
      leg.margin = cleanFormattedNumeric(formValue.legs[i].margin);
    });
    const payload = this.volBasedSaveDealPayload(trades);
    this.saveDeal(payload);
  }

  /*
    If the “Client Premium” is ever changed the “Cash Premium” should be
    recalculated as “Client Premium” * “cashToPremiumStyleDividingAmount”
  */
  private updateCashPremium(i: number) {
    const { clientPremium, ctrls } = this.getValues(i);
    if (this.volBased) {
      const cashToPremiumStyleDividingAmount = ctrls.get(
        'cashToPremiumStyleDividingAmount'
      ).value;
      const cashPremium = +clientPremium * +cashToPremiumStyleDividingAmount;
      ctrls
        .get('cashPremium')
        .setValue(formatNumber(cashPremium, 'en-GB', '1.2-2'));
    } else {
      const latestQuoteEvent = this.latestQuoteEvent();
      const premiumStyleCtrl = this.rfqTradeCaptureParamsForm.get(
        'tradeBookingPremiumStyle'
      );
      if (!premiumStyleCtrl) {
        return;
      }

      const { referenceNotional, referenceCounterNotional } =
        this.referenceNotionals(i, latestQuoteEvent);

      const cashPremium = nonVolBasedCashPremium(
        clientPremium,
        this.currencyPair,
        latestQuoteEvent.quote.premiumCurrency,
        this.rfqTradeCaptureParamsForm.get('tradeBookingPremiumCurrency')
          ?.value,
        this.rfqTradeCaptureParamsForm.get('tradeBookingPremiumStyle')?.value,
        referenceNotional,
        referenceCounterNotional,
        this.rfqTabService.forwardPointsMultiplier()
      );
      ctrls
        .get('cashPremium')
        ?.setValue(formatNumber(cashPremium, 'en-GB', '1.2-2'));
    }
  }

  private referenceNotionals(
    index: number,
    latestQuoteEvent: ClientQuoteEventExtended
  ): { referenceNotional: number; referenceCounterNotional: number } {
    let hydraLegIndex = index;
    const tabName = this.rfqTabService.tabData.title.productName.shortName;
    const quoteLeg = latestQuoteEvent.quote.legs[index];

    if (
      this.rfqTabService.selectedLegs.length === 1 &&
      (tabName === 'straddle' ||
        tabName === 'riskReversal' ||
        tabName === 'strangle')
    ) {
      hydraLegIndex = 0;
    } else if (
      latestQuoteEvent.quote.legs.length !==
      this.rfqTabService.selectedLegs.length
    ) {
      const errorMessage =
        'Number of Hydra legs and quote legs are not the same.';
      this.alertsService.addAlert({ message: errorMessage });
      throw new Error(errorMessage);
    }

    const hydraLeg = this.rfqTabService.selectedLegs[hydraLegIndex].form.value;

    const referenceNotional = getReferenceNotional(
      this.productType,
      quoteLeg.eventSchedule,
      this.currencyPair.base,
      this.currencyPair.term,
      {
        currency: hydraLeg.currency,
        notional: quoteLeg.baseAmount
      }
    );

    const referenceCounterNotional = getReferenceCounterNotional(
      this.productType,
      quoteLeg.eventSchedule,
      this.currencyPair.base,
      this.currencyPair.term,
      {
        currency: hydraLeg.currency,
        counterNotional: quoteLeg.termAmount
      }
    );

    return { referenceNotional, referenceCounterNotional };
  }

  private updateTotals() {
    let totalCashPremium = 0;
    let totalMargin = 0;
    let totalClientPremium = 0;

    (this.acceptedQuoteForm.get('legs') as FormArray).controls.forEach(
      (_quote, i) => {
        const { clientPremium, margin, dir, ctrls } = this.getValues(i);
        totalClientPremium += +clientPremium * -dir;
        totalMargin += +margin * -dir;
        if (ctrls.get('cashPremium')) {
          const cashPremium = cleanFormattedNumeric(
            ctrls.get('cashPremium').value
          );
          totalCashPremium += cashPremium * -dir;
        }
      }
    );

    this.totalMarginDir = totalMargin < 0 ? 'pays' : 'rcvs';
    this.totalCashPremiumDir = totalCashPremium < 0 ? 'pays' : 'rcvs';
    this.totalClientPremiumDir = totalClientPremium < 0 ? 'pays' : 'rcvs';

    this.totalMargin = formatNumber(
      Math.abs(totalMargin),
      'en-GB',
      this.numberFormat
    );
    this.totalCashPremium = formatNumber(
      Math.abs(totalCashPremium),
      'en-GB',
      '1.2-2'
    );
    this.totalClientPremium = formatNumber(
      Math.abs(totalClientPremium),
      'en-GB',
      this.numberFormat
    );
  }

  private enableHedgeEditing(): void {
    const hedge = this.rfqTabService.setupData.hedge;
    const enableHedgeEditing =
      this.configService.userSettings()?.parent[
        'tradeBooking:enableHedgeEditing'
      ];

    const quote = [...this.quotes.bids, ...this.quotes.offers].find(
      quote =>
        quote.status === RFQ_QUOTE_CONFIRM_RFE_ACCEPT &&
        (this.isGenericMultiHedge ? quote.hedgeQuotes : quote.hedge?.amount)
    );
    this.hedgeEditingEnable = enableHedgeEditing && !!quote;

    if (!this.hedgeEditingEnable) {
      return;
    }

    if (this.isGenericMultiHedge) {
      this.setEditableGenericMultiHedges(quote);
    } else {
      this.setEditableHedges(quote);
    }
  }

  private setEditableGenericMultiHedges(quote: RfqQuote): void {
    const dp = this.productsConfigService.decimalPlacesForCross(
      `${this.currencyPair.base}${this.currencyPair.term}`
    );
    this.hedgeQuotes = quote.hedgeQuotes.sort(
      (hedgeA, hedgeB) =>
        new Date(hedgeA.hedgeDeliveryDate).getTime() -
        new Date(hedgeB.hedgeDeliveryDate).getTime()
    );

    const hedges = this.hedgeQuotes.map(hedge => {
      return new FormGroup({
        hedgeAmount: new FormControl(hedge.hedgeAmount.toFixed(0), [
          Validators.required
        ]),
        hedgeRate: new FormControl(hedge.hedgeRate.toFixed(+dp), [
          Validators.required
        ]),
        hedgePosition: new FormControl(hedge.hedgeDirection, [
          Validators.required
        ]),
        executionType: new FormControl(quote.executionType),
        hedgeDeliveryDate: new FormControl(hedge.hedgeDeliveryDate)
      });
    });

    this.acceptedQuoteForm.addControl('hedgeBooking', new FormArray(hedges));
  }

  /**
   * this does nothing yet - waiting for a later CACIB ticket
   * so we know how to handle the response
   */
  public reprice() {
    if (!this.isGenericMultiHedge) {
      return;
    }
    this.rfqTabService
      .submitPricingUpdateRequestEvent(this.hedgeQuotes, this.getHedgeBooking())
      .subscribe({
        next: r => {
          console.log('reprice response', r);
        },
        error: err => {
          console.error('reprice error', err);
        }
      });
  }

  private setEditableHedges(quote: RfqQuote): void {
    if (this.hedgeEditingEnable) {
      const dp = this.productsConfigService.decimalPlacesForCross(
        `${this.currencyPair.base}${this.currencyPair.term}`
      );
      this.acceptedQuoteForm.addControl(
        'bookedHedgeAmount',
        new FormControl(quote.hedge.amount.toFixed(0), [Validators.required])
      );
      this.acceptedQuoteForm.addControl(
        'bookedHedgeRate',
        new FormControl(quote.hedge.value.toFixed(+dp), [Validators.required])
      );

      this.acceptedQuoteForm.addControl(
        'bookedHedgePosition',
        new FormControl(quote.hedge.type, [Validators.required])
      );
    }
  }

  private roundNumberToDp(value: number, roundToFixedDp?: number): string {
    if (roundToFixedDp !== null) {
      return Utilities.formatNumber(value, roundToFixedDp);
    }
    return Utilities.roundNumberToDp(value, this.numberDp, true);
  }

  private get numberFormat(): string {
    const dp = this.numberDp;
    return `1.${dp}-${dp}`;
  }

  private get numberDp(): number {
    return this.premiumStyle === 'percent' ? 4 : 2;
  }

  private getValues(i: number) {
    const fArr = this.acceptedQuoteForm.get('legs') as FormArray;
    const ctrls = fArr.controls[i];
    const payOrReceive = ctrls.get('clientAction').value;
    const dir = payOrReceive === 'Pay' ? 1 : -1;
    const val: Record<string, number | string> = ctrls.getRawValue();
    const {
      premiumValue: premium,
      margin,
      clientPremium,
      retrocessionFee
    } = Object.entries(val).reduce((acc, [k, v]) => {
      acc[k] = cleanFormattedNumeric(v);
      return acc;
    }, {} as any);
    return {
      premium,
      clientPremium,
      margin,
      dir,
      ctrls,
      retrocessionFee: this.useRetrocessionFee ? retrocessionFee : 0
    };
  }

  private getHedges() {
    const tab = this.parentPricesComponent.parentTabComponent;
    return tab.rfqSetupComponent.getHedges();
  }

  private saveDeal(
    payload: SaveNonVolBasedDealPayload | SaveDealPayload
  ): void {
    payload = clone(payload);
    this.entityMapperService.cleanConfidentialFields(payload, 'saveDeal');
    this.rfqService.tradeService(payload).subscribe({
      next: r => {
        this.state.set('BOOKED');
        this.tradesCaptureService.tradeDone();
      },
      error: err => {
        this.alertsService.addAlert({ message: 'Failed to save deal' });
        console.log('failed to save deal', err);
        this.state.set('ERROR');
      },
      complete: () => {
        this.rfqTabService.canCloseWindow = true;
      }
    });
  }

  private adjustQuoteForMargin(): Observable<AdjustQuoteForMargin> {
    this.premiumStyle = 'Amount';
    this.allPremiumDatesSame = true;
    const payload = this.rfqTabService.adjustQuoteForMarginPayload(
      this.rfqTabService.executedTradeInfo.confirmRFEEvent
    );
    if (!payload) {
      return of(null);
    }
    return this.rfqTabService.adjustQuoteForMarginApi(payload);
  }

  public buildNonVolBasedBookingPremiums(updateValues = false): void {
    const latestQuoteEvent = this.latestQuoteEvent();
    const clientQuoteEvent =
      this.rfqTabService.executedTradeInfo.confirmRFEEvent || latestQuoteEvent;
    const adjustedQuote =
      this.AdjustedQuoteForMargin.adjustedClientQuoteEvent.quote;
    const originalQuote = clientQuoteEvent.quote;

    const bidQuote = this.quotes.bids.find(
      quote => quote.status === RFQ_QUOTE_CONFIRM_RFE_ACCEPT
    );
    const tabName = this.rfqTabService.tabData.title.productName.shortName;
    const fArr = this.acceptedQuoteForm.get('legs') as FormArray;
    adjustedQuote.legs.map((leg, index) => {
      let clientAction = null;
      const isInverted = !bidQuote ? leg.inverted : !leg.inverted;
      const quoteLeg = latestQuoteEvent.quote.legs[index];
      let hydraLegIndex = index;

      if (
        this.rfqTabService.selectedLegs.length === 1 &&
        (tabName === 'straddle' ||
          tabName === 'riskReversal' ||
          tabName === 'strangle')
      ) {
        hydraLegIndex = 0;
      } else if (
        adjustedQuote.legs.length !== this.rfqTabService.selectedLegs.length
      ) {
        const errorMessage =
          'Number of Hydra legs and quote legs are not the same.';
        this.alertsService.addAlert({ message: errorMessage });
        throw new Error(errorMessage);
      }

      const { referenceNotional, referenceCounterNotional } =
        this.referenceNotionals(index, latestQuoteEvent);

      const { margin, premiumValue, cashPremium } =
        nonVolBasedPremiumStylesPremiums({
          productType: this.productType,
          adjustedQuote,
          originalQuote,
          isBid: !!bidQuote,
          leg,
          index,
          quoteLeg,
          currencyPair: this.currencyPair,
          tradeBookingPremiumCurrency: this.rfqTradeCaptureParamsForm.get(
            'tradeBookingPremiumCurrency'
          )?.value,
          tradeBookingPremiumStyle: this.rfqTradeCaptureParamsForm.get(
            'tradeBookingPremiumStyle'
          )?.value,
          legConversionRate: this.legConversionRate(index),
          forwardPointsMultiplier: this.rfqTabService.forwardPointsMultiplier(),
          referenceNotional,
          referenceCounterNotional
        });

      if (isInverted) {
        clientAction = premiumValue > 0 ? 'Receive' : 'Pay';
      } else {
        clientAction = premiumValue > 0 ? 'Pay' : 'Receive';
      }

      const posPremiumValue = Math.abs(premiumValue);
      const posCashPremium =
        cashPremium != null
          ? this.roundNumberToDp(Math.abs(cashPremium), 2)
          : null;
      const retrocessionFee =
        adjustedQuote.legs[index]['retrocessionFeeAmount'] || 0;

      const clientPremium = getClientPremium(
        posPremiumValue,
        margin,
        clientAction === 'Pay' ? 1 : -1,
        retrocessionFee
      );

      if (updateValues) {
        fArr.controls[index].setValue({
          clientAction: clientAction as string,
          premiumValue: this.formatPremium(posPremiumValue),
          margin: this.roundNumberToDp(margin, 2),
          retrocessionFee: this.roundNumberToDp(retrocessionFee, 2),
          clientPremium: this.formatPremium(clientPremium),
          premiumCurrency: adjustedQuote.premiumCurrency,
          cashPremium: posCashPremium
        });
      } else {
        fArr.push(
          new FormGroup({
            clientAction: new FormControl(clientAction),
            premiumValue: new FormControl(this.formatPremium(posPremiumValue)),
            margin: new FormControl(this.roundNumberToDp(margin, 2)),
            retrocessionFee: new FormControl(
              this.roundNumberToDp(retrocessionFee, 2)
            ),
            clientPremium: new FormControl(this.formatPremium(clientPremium)),
            premiumCurrency: new FormControl(adjustedQuote.premiumCurrency),
            cashPremium: new FormControl(posCashPremium)
          })
        );
      }
      this.updateClasses(clientPremium, margin, index);
    });
    this.updateTotals();
  }

  private formatPremium(premium: number): string {
    return formatNumber(premium, 'en-GB', this.numberFormat);
  }

  private volBasedSaveDealPayload(
    trades: (TradeBookingParametersLeg | TradeBookingParametersHedge)[]
  ): SaveDealPayload {
    const premiumCurrency = this.premiumCurrency;
    const premiumStyle = this.premiumStyle;
    const subAccount = this.rfqTabService.selectedSubAccount;
    const bookedHedgeAmount = cleanFormattedNumeric(
      this.acceptedQuoteForm.get('bookedHedgeAmount')?.value
    );
    const bookedHedgeRate = this.acceptedQuoteForm.get('bookedHedgeRate');
    const payload: SaveDealPayload = {
      task: 'saveDeal',
      trades,
      premiumCurrency,
      premiumStyle,
      subAccount,
      franchiseCredit: {
        franchiseCreditInFranchiseCreditCurrency: cleanFormattedNumeric(
          this.acceptedQuoteForm.get('franchiseCreditInFranchiseCreditCurrency')
            .value
        ),
        franchiseCreditCurrency: this.franchiseCredit.franchiseCreditCurrency
      },
      bookedHedgeAmount,
      bookedHedgeRate: bookedHedgeRate ? +bookedHedgeRate.value : null,
      hedgeBooking: this.getHedgeBooking()
    };

    return payload;
  }

  private getHedgeBooking(): HedgeQuoteMessage {
    const quote = this.getAcceptedQuote();
    const hedgeMessage = this.rfqTabService
      .hedgeQuoteMessages()
      .get(quote.quoteReference);
    if (this.acceptedQuoteForm.get('hedgeBooking') && hedgeMessage) {
      const clonedHedgeQuoteMessage = cloneDeep(hedgeMessage);
      const cleanHedges = cleanBookedHedges(
        this.acceptedQuoteForm.get('hedgeBooking') as FormArray
      );

      clonedHedgeQuoteMessage.hedges = cloneDeep(this.hedgeQuotes).map(
        (hedge, index) => {
          return {
            ...hedge,
            ...cleanHedges[index]
          };
        }
      );
      return clonedHedgeQuoteMessage;
    }
    return undefined;
  }

  private getAcceptedQuote(): RfqQuote {
    return [...this.quotes.bids, ...this.quotes.offers].find(
      quote => quote.status === RFQ_QUOTE_CONFIRM_RFE_ACCEPT
    );
  }

  private updateClasses(
    clientPremium: number,
    margin: number,
    index: number
  ): void {
    this.clientPremiumClass[index] = +clientPremium < 0 ? 'invalid' : 'valid';
    this.marginClass[index] = +margin < 0 ? 'negative' : 'positive';
  }
}
